<!-- eslint-disable vue/no-multiple-template-root -->
<template>
  <Html :lang="head.htmlAttrs?.lang">
    <Head>
      <template v-for="link in head.link" :key="link.id">
        <Link :id="link.id" :rel="link.rel" :href="link.href" :hreflang="link.hreflang" />
      </template>
      <template v-for="meta in head.meta" :key="meta.id">
        <Meta :id="meta.id" :content="meta.content" :property="meta.property" />
      </template>
    </Head>
  </Html>

  <NuxtPwaManifest />

  <v-app>
    <v-layout>
      <v-navigation-drawer
        v-model="leftDrawerOpen"
        :rail="miniDrawer && $vuetify.display.smAndUp"
        :permanent="$vuetify.display.width > 720"
        :color="currentTheme.dark ? 'secondary' : 'primary'"
        name="nav-drawer"
      >
        <template #prepend>
          <layout-side-menu-header
            :user="currentUser"
            :rail="miniDrawer && $vuetify.display.smAndUp"
            @update:left-drawer-open="
              () => {
                if ($vuetify.display.xs) {
                  leftDrawerOpen = !leftDrawerOpen
                } else {
                  miniDrawer = !miniDrawer
                }
              }
            "
          />
        </template>
        <template #default><layout-side-menu :rail="miniDrawer && $vuetify.display.smAndUp" /></template>
        <template #append><layout-side-menu-footer :rail="miniDrawer && $vuetify.display.smAndUp" /></template>
      </v-navigation-drawer>

      <v-app-bar color="tertiary" class="text-white" :density="$vuetify.display.smAndDown ? 'compact' : 'default'">
        <v-app-bar-nav-icon v-if="$route.meta.back" icon="arrow_back" @click="() => $router.back()" />
        <v-app-bar-nav-icon v-else-if="$vuetify.display.xs" icon="menu" @click="() => (leftDrawerOpen = !leftDrawerOpen)" />
        <v-app-bar-nav-icon
          v-else-if="miniDrawer || !leftDrawerOpen"
          icon="menu"
          @click="
            () => {
              miniDrawer = false
              leftDrawerOpen = true
            }
          "
        />

        <v-toolbar-title v-if="breadcrumbs.length && $vuetify.display.mdAndUp">
          <v-breadcrumbs
            class="px-0 navigation-breadcrumbs"
            :items="
              breadcrumbs.map((breadcrumb, index, self) => ({
                ...breadcrumb,
                title: self.length - 1 === index ? title : $i18n.t(breadcrumb.title),
                disabled: self.length - 1 === index,
              }))
            "
          />
        </v-toolbar-title>
        <v-toolbar-title v-else>{{ title }}</v-toolbar-title>

        <template #append>
          <v-dialog v-if="$vuetify.display.smAndDown" width="400">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon="search" />
            </template>

            <template #default="{ isActive }">
              <global-search
                variant="solo"
                single-line
                autofocus
                :placeholder="$t('search')"
                @select-asset="() => (isActive.value = false)"
              />
            </template>
          </v-dialog>

          <global-search v-else rounded variant="outlined" density="compact" class="mr-4" style="width: 300px" />

          <div class="d-flex align-center ga-3 mr-4">
            <add-menu />

            <v-menu :close-on-content-click="false">
              <template #activator="{ props }">
                <v-btn v-if="currentUser?.firstName" icon v-bind="props" color="white" density="comfortable">
                  <v-avatar color="white" size="36" class="text-body-medium">{{ initials }}</v-avatar>
                </v-btn>
                <v-btn
                  v-else
                  :icon="isImpersonated ? 'supervised_user_circle' : 'account_circle'"
                  v-bind="props"
                  density="comfortable"
                  color="white"
                >
                  <v-avatar color="white" size="36" class="text-body-medium">
                    <v-icon :icon="isImpersonated ? 'visibility' : 'person'" color="tertiary" />
                  </v-avatar>
                </v-btn>
              </template>

              <template #default="{ isActive }">
                <header-menu @close="() => (isActive.value = false)" />
              </template>
            </v-menu>
            <div id="header-menu-backdrop-target" />
          </div>
        </template>

        <template v-if="tabs.length > 0" #extension>
          <v-tabs :key="tabsKey" v-model="selectedTab" :class="$vuetify.display.mdAndUp ? 'px-4' : ''">
            <v-tab
              v-for="tab of tabs"
              :key="tab.value"
              :value="tab.value"
              :prepend-icon="tab.icon"
              class="text-none"
              :disabled="tab.scope && !hasScope(organizationId, tab.scope)"
            >
              <v-tooltip v-if="tab.tooltip" location="bottom">
                <template #activator="{ props: tooltipProps }">
                  <span v-bind="tooltipProps">
                    {{ $te(tab.label) ? $t(tab.label) : tab.label }}
                  </span>
                </template>
                <div class="d-flex flex-column">
                  <span v-if="getTabTooltip(tab.tooltip)?.title" class="text-title-small font-weight-bold">
                    {{ getTabTooltip(tab.tooltip)?.title }}
                  </span>
                  <span class="text-body-medium text-body-small">{{ getTabTooltip(tab.tooltip)?.text }}</span>
                </div>
              </v-tooltip>
              <span v-else>
                {{ $te(tab.label) ? $t(tab.label) : tab.label }}
              </span>
            </v-tab>
          </v-tabs>
        </template>
      </v-app-bar>

      <map-drawer v-bind="mapProps" />

      <v-main scrollable name="main-content">
        <v-progress-linear
          v-if="currentUserStatus === 'pending' || scopesStatus === 'pending'"
          indeterminate
          class="position-absolute top-0"
        />
        <slot v-if="currentUserStatus !== 'pending' && scopesStatus !== 'pending'" />

        <snackbar-toaster :snackbars="snackbars" />
      </v-main>

      <v-theme-provider theme="dark">
        <v-bottom-navigation v-if="display.xs.value" grow bg-color="tertiary">
          <v-btn to="/dashboard">
            <v-icon icon="$dashboard" />
            <span>Home</span>
          </v-btn>
          <v-btn to="/assets">
            <v-icon icon="$asset" />
            <span>{{ $t("assets.title") }}</span>
          </v-btn>
          <v-btn to="/tickets">
            <v-icon icon="$ticket" />
            <span>{{ $t("tickets.title") }}</span>
          </v-btn>
        </v-bottom-navigation>
      </v-theme-provider>

      <div id="add-menu-backdrop-target" />
    </v-layout>
  </v-app>
</template>

<script setup lang="ts">
import { useDisplay, useTheme } from "vuetify"

const { current: currentTheme } = useTheme()
const display = useDisplay()
const $route = useRoute()
const $router = useRouter()
const $i18n = useI18n()

const userStore = useUserStore()
const authStore = useAuthStore()
const mainStore = useMainStore()

const { initials, currentOrganization, isImpersonated } = storeToRefs(authStore)
const { snackbars } = storeToRefs(mainStore)

const { miniDrawer } = useUserPreferences()

useThemeWatcher()

const leftDrawerOpen = ref(display.lgAndUp.value)
const mapProps = ref<Record<string, unknown>>({})

const { hasScope } = authStore
const mutateMapProps = (settings: Record<string, unknown>) => {
  mapProps.value = settings
}

provide("mapProps", mapProps)
provide("mutateMapProps", mutateMapProps)

const { computedTitle: title } = useRouteTitle()

const organizationId = computed(() => {
  const organizationId = $route.meta.organizationId as string | undefined
  if (!organizationId) {
    return currentOrganization.value!
  }

  return organizationId
})

const head = useLocaleHead()

const breadcrumbs = computed<Array<BreadcrumbItem>>(() =>
  $route.meta.breadcrumbs && Array.isArray($route.meta.breadcrumbs) ? $route.meta.breadcrumbs : [],
)
const { tabs, selectedTab, tabsKey } = useTabs()

const translateTabValue = (value: string) => ($i18n.te(value) ? $i18n.t(value) : value)

const getTabTooltip = (tooltip?: TabItem["tooltip"]) => {
  if (!tooltip) return

  if (Array.isArray(tooltip)) {
    const [first, second] = tooltip

    return {
      title: second ? translateTabValue(first) : undefined,
      text: translateTabValue(second ?? first),
    }
  }
  return {
    title: undefined,
    text: translateTabValue(tooltip),
  }
}

const { data: currentUser, status: currentUserStatus } = useAsyncData(() => userStore.getCurrentUser(), { immediate: true })
const { status: scopesStatus } = useAsyncData(() => authStore.fetchAllOrganizationScopes(), { immediate: true })
</script>

<style lang="scss">
.v-navigation-drawer__content {
  &::-webkit-scrollbar {
    width: 8px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    z-index: 999;
    position: absolute;

    &:hover {
      background-color: rgba(255, 255, 255, 0.4);
    }
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
}

.navigation-breadcrumbs .v-breadcrumbs-item--disabled {
  opacity: inherit;
}
</style>
